<template>
  <div :class="$style.wrapper">
    <section v-if="seller && !isLoading" :class="$style.profileHeader">
      <div :class="$style.avatarLarge">
        <img v-if="seller.photo_url" :src="seller.photo_url" alt="" />
        <span v-else>{{ sellerInitial }}</span>
      </div>
      <h1 :class="$style.name">{{ seller.login || seller.username }}</h1>
      <div :class="$style.subline">
        <span>Селлер</span>
        <template v-if="seller.rating_count > 0">
          <span :class="$style.dot">•</span>
          <span :class="$style.ratingText">
            {{ sellerRating ?? "—" }}
            <img
              src="@/assets/img/account/star.svg"
              alt=""
              :class="$style.star"
            />
          </span>
        </template>
      </div>
    </section>
    <div v-else-if="isLoading" :class="$style.profileSkeleton">
      <div :class="['skeleton', $style.avatarSkeleton]"></div>
      <div :class="['skeleton', $style.nameSkeleton]"></div>
      <div :class="['skeleton', $style.sublineSkeleton]"></div>
    </div>
    <div v-else :class="$style.placeholder">Продавец не найден</div>

    <div :class="$style.topBar">
      <div :class="$style.searchBox">
        <input
          v-model="q"
          @input="handleSearch"
          type="text"
          placeholder="Поиск по товарам"
        />
        <img src="@/assets/img/market/search.svg" alt="" />
      </div>

      <button
        :class="[$style.iconBtn, { [$style.iconBtnActive]: hasFilters }]"
        type="button"
        @click="filtersOpen = true"
      >
        <img src="@/assets/img/market/filters.svg" alt="" />
      </button>

      <button :class="$style.iconBtn" type="button" @click="openSort">
        <img src="@/assets/img/market/sort.svg" alt="" />
      </button>

      <button
        v-if="$store.state.user.type === 'reseller'"
        :class="[$style.iconBtn, $style.cartBtn, isBidDisabled && $style.disabled]"
        type="button"
        :disabled="isBidDisabled"
        @click="openBid"
      >
        <svg
          width="21"
          height="21"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M5.5 8h13l1.1 11.3a1.7 1.7 0 0 1-1.7 1.9H6.1a1.7 1.7 0 0 1-1.7-1.9L5.5 8Z"
          />
          <path d="M9 10.5V6a3 3 0 0 1 6 0v4.5" />
        </svg>
        <span v-if="bidPairsCount" :class="$style.cartBadge">
          {{ bidPairsCount }}
        </span>
      </button>
    </div>

    <div v-if="!isLoading && stats" :class="$style.statsGrid">
      <div v-for="stat in statCards" :key="stat.label" :class="$style.statCard">
        <strong>{{ stat.value }}</strong>
        <span>{{ stat.label }}</span>
      </div>
    </div>
    <div v-else-if="isLoading" :class="$style.statsGrid">
      <div v-for="n in 4" :key="`stat-skeleton-${n}`" :class="$style.statCard">
        <strong :class="['skeleton', $style.statValueSkeleton]"></strong>
        <span :class="['skeleton', $style.statLabelSkeleton]"></span>
      </div>
    </div>

    <div v-if="isLoading" :class="$style.cardsGrid">
      <div
        v-for="n in 6"
        :key="`seller-card-skeleton-${n}`"
        :class="$style.cardSkeleton"
      >
        <div :class="['skeleton', $style.cardImageSkeleton]"></div>
        <div :class="['skeleton', $style.cardLineSkeleton]"></div>
        <div :class="['skeleton', $style.cardLineSkeletonShort]"></div>
      </div>
    </div>
    <div v-else-if="error" :class="$style.errorMsg">{{ error }}</div>
    <div v-else>
      <div v-if="products.length" :class="$style.cardsGrid">
        <div
          v-for="product in products"
          :key="product.id"
          :class="$style.cardWrapper"
        >
          <button
            :class="$style.favBtn"
            type="button"
            @click.stop="toggleFavorite(product)"
          >
            <img
              v-if="!product.isFavorite"
              src="@/assets/img/market/like.svg"
              alt=""
            />
            <img v-else src="@/assets/img/market/fill-like.svg" alt="" />
          </button>
          <div :class="$style.cardClickable" @click="openModel(product.id)">
            <Card :item="product.cardItem" />
          </div>
        </div>
      </div>
      <div v-else :class="$style.empty">Нет товаров</div>
      <div v-if="isLoadingMore" :class="$style.loadingMore">
        <div :class="$style.loadingSpinner"></div>
      </div>
    </div>

    <FiltersModal
      ref="filtersModal"
      v-if="filtersOpen"
      :filters="filters"
      :show-category-filter="true"
      :use-global-back-handler="true"
      @close="filtersOpen = false"
      @apply="applyFilters"
      @open-brands="openBrandsTab"
    />

    <div
      v-if="sortModal"
      :class="[$style.sortBg, { [$style.fadeOut]: sortClosing }]"
      @click="closeSort"
    >
      <div
        :class="[$style.sortModal, { [$style.slideDown]: sortClosing }]"
        @click.stop
      >
        <div :class="$style.sortHeader"></div>
        <div :class="$style.sortTitle">Сортировка</div>

        <div :class="$style.sortList">
          <div
            v-for="option in sortOptions"
            :key="option.value"
            :class="$style.sortItem"
            @click="applySort(option.value)"
          >
            <span>{{ option.label }}</span>
            <div
              :class="[
                $style.radio,
                { [$style.radioActive]: sort === option.value },
              ]"
            >
              <img
                v-if="sort === option.value"
                src="@/assets/img/market/check.svg"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Card from "@/components/Card.vue"
import FiltersModal from "@/components/FiltersModal.vue"
import placeholderImage from "@/assets/img/logo.svg"

export default {
  name: "Seller",
  components: { FiltersModal, Card },
  data() {
    return {
      seller: null,
      stats: null,
      products: [],
      productsTotal: 0,

      isLoading: true,
      isLoadingMore: false,
      hasMore: true,
      limit: 20,
      offset: 0,
      error: "",

      q: "",
      searchTimer: null,

      filtersOpen: false,
      filters: {
        brand: [],
        color: [],
        size: [],
        size_type: "EU", // сетка EU выделена по умолчанию
        custom: null,
        category: [],
        attrs: {}, // характеристики: {sport: [...], material: [...], ...}
      },

      sort: "random",
      sortModal: false,
      sortClosing: false,
      sortOptions: [
        { label: "По цене ↑", value: "price_asc" },
        { label: "По цене ↓", value: "price_desc" },
        { label: "Сначала новые", value: "date_desc" },
        { label: "Сначала старые", value: "date_asc" },
        { label: "Популярные", value: "random" },
      ],

      filtersBackHandlerRef: null,
      sortBackHandlerRef: null,
    }
  },
  computed: {
    sellerId() {
      return this.$route.params.id
    },
    user() {
      return this.$store.state.user || {}
    },
    isBidDisabled() {
      return this.user.moderation_status === "pending"
    },
    // Характеристики для payload: только непустые группы
    attrFilterParams() {
      const params = {}
      for (const [key, values] of Object.entries(this.filters.attrs || {})) {
        if (Array.isArray(values) && values.length > 0) {
          params[key] = values
        }
      }
      return params
    },
    // Число пар в промежуточной корзине — бейдж иконки
    bidPairsCount() {
      return this.$store.getters.bidPairsCount
    },
    sellerInitial() {
      return (
        (this.seller?.login || this.seller?.username)?.[0]?.toUpperCase() || "S"
      )
    },
    sellerRating() {
      if (!this.seller || this.seller.rating === null) return null
      const value = Number(this.seller.rating)
      return Number.isNaN(value) ? null : value.toFixed(1)
    },
    hasFilters() {
      return (
        this.filters.brand.length > 0 ||
        this.filters.color.length > 0 ||
        this.filters.size.length > 0 ||
        Object.values(this.filters.attrs || {}).some(
          (a) => Array.isArray(a) && a.length > 0
        ) ||
        this.filters.custom !== null ||
        (this.filters.category && this.filters.category.length > 0)
      )
    },
    daysOnPlatformText() {
      const created = this.seller?.created_at
      if (!created) return "—"
      const start = new Date(created).getTime()
      if (Number.isNaN(start)) return "—"
      const now = Date.now()
      const days = Math.floor((now - start) / (24 * 60 * 60 * 1000))
      if (days < 0) return "—"
      if (days >= 365) {
        const years = Math.round(days / 365)
        const n = years % 100
        const n1 = n % 10
        if (n > 10 && n < 20) return `${years} лет`
        if (n1 === 1) return `${years} год`
        if (n1 >= 2 && n1 <= 4) return `${years} года`
        return `${years} лет`
      }
      const n = days % 100
      const n1 = n % 10
      if (n > 10 && n < 20) return `${days} дней`
      if (n1 === 1) return `${days} день`
      if (n1 >= 2 && n1 <= 4) return `${days} дня`
      return `${days} дней`
    },
    statCards() {
      if (!this.stats) return []
      return [
        {
          value: this.daysOnPlatformText,
          label: "На платформе",
        },
        {
          value: this.stats.total_products,
          label: "Товаров",
        },
        {
          value: this.stats.total_deals,
          label: "Сделок всего",
        },
        {
          value: this.stats.total_cancelled_deals,
          label: "Отменённых заказов",
        },
      ]
    },
  },
  watch: {
    sellerId() {
      this.resetState()
      this.initPage()
    },
    filtersOpen(value) {
      if (value) {
        this.registerFiltersBackHandler()
      } else {
        this.unregisterFiltersBackHandler()
      }
    },
    sortModal(value) {
      if (value) {
        this.registerSortBackHandler()
      } else {
        this.unregisterSortBackHandler()
      }
    },
  },
  async mounted() {
    await this.initPage()
    window.addEventListener("scroll", this.handleScroll)
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer)
    this.unregisterFiltersBackHandler()
    this.unregisterSortBackHandler()
    window.removeEventListener("scroll", this.handleScroll)
  },
  methods: {
    async initPage() {
      this.isLoading = true
      await this.loadSellerData(true)
    },
    resetState() {
      this.seller = null
      this.stats = null
      this.products = []
      this.productsTotal = 0
      this.q = ""
      this.filters = {
        brand: [],
        color: [],
        size: [],
        size_type: "EU",
        custom: null,
        category: [],
        attrs: {},
      }
      this.sort = "random"
      this.error = ""
      this.offset = 0
      this.hasMore = true
    },
    buildPayload() {
      return {
        userId: this.sellerId,
        q: this.q || undefined,
        brand: this.filters.brand.length > 0 ? this.filters.brand : undefined,
        color: this.filters.color.length > 0 ? this.filters.color : undefined,
        size: this.filters.size.length > 0 ? this.filters.size : undefined,
        size_type:
          this.filters.size.length > 0
            ? this.filters.size_type || undefined
            : undefined,
        is_custom:
          this.filters.custom !== null ? this.filters.custom : undefined,
        category:
          this.filters.category?.length > 0 ? this.filters.category : undefined,
        ...this.attrFilterParams,
        sort: this.sort,
        limit: this.limit,
        offset: this.offset,
      }
    },
    async loadSellerData(reset = false) {
      if (!this.sellerId) return

      if (reset) {
        this.offset = 0
        this.hasMore = true
        this.isLoading = true
      } else {
        if (!this.hasMore || this.isLoadingMore) return
        this.isLoadingMore = true
      }

      this.error = ""

      try {
        const response = await this.$store.dispatch(
          "getSellerInfo",
          this.buildPayload()
        )
        const data = response?.seller ? response : response?.data || {}

        if (!data?.seller) {
          throw new Error("Нет данных по продавцу")
        }

        if (reset) {
          this.seller = data.seller
          this.stats = data.stats
          this.productsTotal = data.products?.total || 0
        }

        const items = data.products?.items || []
        // Одна карточка на модель: min_price/total_count — агрегаты по всем
        // размерам селлера; id = model_id (клик → страницу модели),
        // productId — самое дешёвое предложение (кнопка «избранное»).
        const mappedProducts = items.map((item) => ({
          id: item.model_id,
          productId: item.product_id,
          isFavorite: item.is_favorite,
          cardItem: {
            title: item.model?.title || `Модель #${item.model_id}`,
            images:
              Array.isArray(item.model?.images) && item.model.images.length > 0
                ? item.model.images
                : [],
            preview_image:
              item.model?.preview_image ||
              item.model?.images?.[0] ||
              placeholderImage,
            is_new: item.is_new,
            is_trade: item.is_trade,
            total_count: item.total_count,
            min_price: item.min_price,
          },
        }))

        if (reset) {
          this.products = mappedProducts
        } else {
          this.products = [...this.products, ...mappedProducts]
        }

        // Проверяем, есть ли еще данные
        this.hasMore = items.length === this.limit
        if (this.hasMore) {
          this.offset += this.limit
        }
      } catch (err) {
        console.error(err)
        this.error = "Не удалось загрузить данные продавца"
      } finally {
        this.isLoading = false
        this.isLoadingMore = false
      }
    },
    handleScroll() {
      if (this.isLoading || this.isLoadingMore || !this.hasMore) return

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight

      // Загружаем, когда осталось 200px до конца
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.loadSellerData(false)
      }
    },
    handleSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.loadSellerData(true)
      }, 400)
    },
    async applyFilters(newFilters) {
      this.filters = newFilters
      this.closeFiltersModal()
      await this.loadSellerData(true)
    },
    openSort() {
      this.sortClosing = false
      this.sortModal = true
    },
    closeSort() {
      this.sortClosing = true
      setTimeout(() => {
        this.sortModal = false
        this.sortClosing = false
      }, 250)
    },
    async applySort(value) {
      this.sort = value
      this.closeSort()
      await this.loadSellerData(true)
    },
    async toggleFavorite(product) {
      // Сохраняем исходное состояние
      const originalState = product.isFavorite

      // Находим индекс продукта в массиве
      const productIndex = this.products.findIndex((p) => p.id === product.id)

      // Оптимистично обновляем состояние сразу
      if (productIndex !== -1) {
        this.products[productIndex].isFavorite = !originalState
      }

      try {
        if (!originalState) {
          await this.$store.dispatch("addFavorite", {
            product_id: product.productId ?? product.id,
          })
        } else {
          await this.$store.dispatch("deleteFavorite", {
            product_id: product.productId ?? product.id,
          })
        }
      } catch (err) {
        // В случае ошибки возвращаем предыдущее состояние
        if (productIndex !== -1) {
          this.products[productIndex].isFavorite = originalState
        }
        console.error("Ошибка при изменении избранного:", err)
      }
    },
    openModel(id) {
      this.$router.push({
        name: "Model",
        params: { id },
      })
    },
    closeFiltersModal() {
      if (this.$refs.filtersModal) {
        this.$refs.filtersModal.startClose()
      } else {
        this.filtersOpen = false
      }
    },
    openBrandsTab() {
      this.closeFiltersModal()
      this.$router.push({
        name: "market",
        query: { tab: "brands" },
      })
    },
    registerFiltersBackHandler() {
      if (this.filtersBackHandlerRef || !window.__AppBackHandlerStack) return
      this.filtersBackHandlerRef = () => {
        this.closeFiltersModal()
        return true
      }
      window.__AppBackHandlerStack.push(this.filtersBackHandlerRef)
    },
    unregisterFiltersBackHandler() {
      if (!this.filtersBackHandlerRef) return
      window.__AppBackHandlerStack?.pop(this.filtersBackHandlerRef)
      this.filtersBackHandlerRef = null
    },
    registerSortBackHandler() {
      if (this.sortBackHandlerRef || !window.__AppBackHandlerStack) return
      this.sortBackHandlerRef = () => {
        this.closeSort()
        return true
      }
      window.__AppBackHandlerStack.push(this.sortBackHandlerRef)
    },
    unregisterSortBackHandler() {
      if (!this.sortBackHandlerRef) return
      window.__AppBackHandlerStack?.pop(this.sortBackHandlerRef)
      this.sortBackHandlerRef = null
    },
    openBid() {
      if (this.isBidDisabled) {
        return false
      }
      this.$router.push({ name: "Bid" })
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 1.25rem 1rem 6.5rem 1rem;
}

.profileHeader {
  display: grid;
  justify-items: center;
  margin-bottom: 2.125rem;
  text-align: center;
}

.avatarLarge {
  width: 5.625rem;
  height: 5.625rem;
  border-radius: 50%;
  background: #111;
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 2.25rem;
  font-weight: 500;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.name {
  margin-top: 0.75rem;
  font-size: 1.5rem;
  line-height: 1.2;
  font-weight: 600;
  color: #000;
  text-align: center;
}

.subline {
  display: inline-flex;
  align-items: center;
  color: #9b9ba1;
  font-size: 1rem;
  margin: 0.25rem 0 0.5rem 0;
  .rating {
    color: #000;
  }
}

.dot {
  opacity: 0.7;
  padding: 0 0.5rem;
}

.ratingText {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #000;
  font-weight: 600;
}

.star {
  width: 1rem;
  height: 1rem;
  fill: #ffb800;
  margin: 0 0 0 0.25rem;
}

.topBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 0.375rem;
  min-width: 0;
}

.searchBox {
  flex: 1;
  min-width: 0;
  border: 1px solid #b8b8bc5c;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  input {
    flex: 1;
    min-width: 0;
    border: none;
    background: transparent;
    outline: none;
    font-size: 0.875rem;
    width: 0;
  }
  img {
    width: 1.5rem;
    height: 1.5rem;
    flex-shrink: 0;
  }
}

.iconBtn {
  width: 2.75rem;
  height: 2.75rem;
  min-width: 2.75rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #b8b8bc5c;
  border-radius: 0.5rem;
  background: #fff;

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.iconBtnActive {
  border-color: #000;
}

/* Кнопка корзины с числовым бейджем */
.cartBtn {
  position: relative;
  color: #161616;
}

.cartBadge {
  position: absolute;
  top: -0.375rem;
  right: -0.3125rem;
  min-width: 1.0625rem;
  height: 1.0625rem;
  padding: 0 0.2rem;
  border-radius: 0.5625rem;
  background: #ec6608;
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  pointer-events: none;
}

.statsGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.statCard {
  background: #f5f7fa;
  border-radius: 0.625rem;
  padding: 0.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.statCard strong {
  font-size: 1.25rem;
  font-weight: 600;
  color: #000000;
}

.statCard span {
  font-size: 0.875rem;
  color: #000000;
  opacity: 0.3;
}

.cardsGrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
}

.cardWrapper {
  position: relative;
  border-radius: 0.75rem;
  background: #fff;
}

.profileSkeleton {
  display: grid;
  justify-items: center;
  gap: 0.5rem;
  margin-bottom: 2.125rem;
}

.avatarSkeleton {
  width: 5.625rem;
  height: 5.625rem;
  border-radius: 50%;
}

.nameSkeleton {
  width: 60%;
  height: 1.25rem;
  border-radius: 0.5rem;
}

.sublineSkeleton {
  width: 80%;
  height: 0.875rem;
  border-radius: 0.5rem;
}

.statValueSkeleton {
  display: block;
  width: 60%;
  height: 1.25rem;
  border-radius: 0.5rem;
}

.statLabelSkeleton {
  display: block;
  width: 70%;
  height: 0.75rem;
  border-radius: 0.5rem;
}

.cardSkeleton {
  border-radius: 0.75rem;
  padding: 0.5rem;
  background: #fff;
  border: 1px solid #b8b8bc5c;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cardImageSkeleton {
  width: 100%;
  height: 5.5rem;
  border-radius: 0.5rem;
}

.cardLineSkeleton {
  width: 100%;
  height: 0.875rem;
  border-radius: 0.5rem;
}

.cardLineSkeletonShort {
  width: 60%;
  height: 0.75rem;
  border-radius: 0.5rem;
}

.cardClickable {
  cursor: pointer;
}

.favBtn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  border: none;
  background: none;
  padding: 0;
  z-index: 2;
}

.placeholder,
.empty,
.errorMsg {
  margin: 1rem 0;
  text-align: center;
  color: #7b7b81;
}

.errorMsg {
  color: #d53a3a;
}

/* Sort modal reuse */
.sortBg {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1000;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    backdrop-filter: blur(10px);
  }
  to {
    opacity: 0;
    backdrop-filter: blur(0);
  }
}

.fadeOut {
  animation: fadeOut 0.25s ease forwards;
}

.sortModal {
  width: 100%;
  background: #fff;
  border-radius: 1.5rem 1.5rem 0 0;
  padding: 1.5rem 1rem;
  animation: slideUp 0.25s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(40px);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes slideDownAnim {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(40px);
    opacity: 0;
  }
}

.slideDown {
  animation: slideDownAnim 0.25s ease forwards;
}

.sortHeader {
  width: 3.25rem;
  height: 0.375rem;
  background: #ddd;
  border-radius: 0.5rem;
  margin: 0 auto 0.875rem;
}

.sortTitle {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
}

.sortList {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sortItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f7f7f7;
  border-radius: 0.5rem;
}

.radio {
  position: relative;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 1px solid #bbb;
}

.radioActive {
  background: #f07818;
  border-color: #f07818;
}

.radio img {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.loadingMore {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

.loadingSpinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
